import React, { useState, useEffect, useRef } from 'react';

interface TextBandEditorProps {
  imageFile: File;
  onTextOverlayComplete: (overlayedImage: File) => void;
  onCancel: () => void;
}

export const TextBandEditor: React.FC<TextBandEditorProps> = ({
  imageFile,
  onTextOverlayComplete,
  onCancel
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Band settings
  const [position, setPosition] = useState<'top' | 'center' | 'bottom'>('top');
  const [bgColor, setBgColor] = useState('#000000');
  const [bgOpacity, setBgOpacity] = useState(0.7);
  const [bandHeight, setBandHeight] = useState(150);

  // Load image or video
  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    const fileIsVideo = imageFile.type.startsWith('video/');

    setIsVideo(fileIsVideo);

    if (fileIsVideo) {
      // Handle video
      setVideoUrl(url);
      setImageLoaded(true); // Use same flag for simplicity
    } else {
      // Handle image
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setImageLoaded(true);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    }

    return () => {
      if (fileIsVideo && videoUrl) {
        URL.revokeObjectURL(videoUrl);
      } else if (!fileIsVideo) {
        URL.revokeObjectURL(url);
      }
    };
  }, [imageFile]);

  // Render preview for images only
  useEffect(() => {
    if (isVideo) return; // Skip for videos
    if (!canvasRef.current || !originalImage || !imageLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match image
    canvas.width = originalImage.width;
    canvas.height = originalImage.height;

    // Draw original image
    ctx.drawImage(originalImage, 0, 0);

    // Calculate band position
    let bandY = 0;

    if (position === 'top') {
      bandY = 0;
    } else if (position === 'center') {
      bandY = (canvas.height - bandHeight) / 2;
    } else {
      bandY = canvas.height - bandHeight;
    }

    // Draw background band
    ctx.fillStyle = `rgba(${parseInt(bgColor.slice(1, 3), 16)}, ${parseInt(bgColor.slice(3, 5), 16)}, ${parseInt(bgColor.slice(5, 7), 16)}, ${bgOpacity})`;
    ctx.fillRect(0, bandY, canvas.width, bandHeight);
  }, [isVideo, originalImage, imageLoaded, position, bgColor, bgOpacity, bandHeight]);

  const handleApply = async () => {
    if (isVideo) {
      // For videos, we can't burn in the overlay in the browser
      // In a production app, you'd send the video + band settings to a server for processing
      // For now, just return the original video file
      alert('Note: Video band overlay preview only. The band is not burned into the video file. Server-side processing required for permanent overlay.');
      onTextOverlayComplete(imageFile);
      return;
    }

    if (!canvasRef.current) return;

    // Convert canvas to blob (for images)
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;

      // Create new file with overlay
      const overlayedFile = new File(
        [blob],
        imageFile.name.replace(/\.(jpg|jpeg|png|webp)$/i, '_text.webp'),
        { type: 'image/webp' }
      );

      onTextOverlayComplete(overlayedFile);
    }, 'image/webp', 0.95);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        padding: '24px'
      }}>
        {/* Preview Panel */}
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ margin: 0 }}>Preview</h3>
          <div style={{
            border: '1px solid rgba(55, 53, 47, 0.16)',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            position: 'relative'
          }}>
            {isVideo && videoUrl ? (
              // Video preview with overlay
              <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '70vh' }}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  loop
                  muted
                  autoPlay
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    display: 'block'
                  }}
                />
                {/* Band overlay */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: `${bandHeight}px`,
                    backgroundColor: bgColor,
                    opacity: bgOpacity,
                    pointerEvents: 'none',
                    top: position === 'top' ? 0 : position === 'center' ? '50%' : 'auto',
                    bottom: position === 'bottom' ? 0 : 'auto',
                    transform: position === 'center' ? 'translateY(-50%)' : 'none'
                  }}
                />
              </div>
            ) : (
              // Image preview with canvas
              <canvas
                ref={canvasRef}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        </div>

        {/* Controls Panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Band Editor</h3>
          </div>

          {/* Position */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
              Position
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['top', 'center', 'bottom'] as const).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosition(pos)}
                  className={position === pos ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ flex: 1, fontSize: '12px', textTransform: 'capitalize' }}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          {/* Band Height */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
              Band Height: {bandHeight}px
            </label>
            <input
              type="range"
              min="50"
              max="400"
              value={bandHeight}
              onChange={(e) => setBandHeight(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          {/* Background Color */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
              Background Color
            </label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              style={{ width: '100%', height: '40px', cursor: 'pointer' }}
            />
          </div>

          {/* Background Opacity */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
              Background Opacity: {Math.round(bgOpacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={bgOpacity}
              onChange={(e) => setBgOpacity(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}>
            <button
              onClick={onCancel}
              className="btn btn-secondary"
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              Apply Band
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
